import React, { useState } from 'react';
import Popover from './Popover';
import Button from './Button';
import FilterValueEditor from './FilterValueEditor';
import { Icon } from '../icons';
import { formatFilterValue, isFilterEmpty } from './filterModel';
import { useT } from '../../i18n';

/**
 * Inner panel content — its own mount each time the popover opens, so the
 * step state resets cleanly. Two views: the field picker and a value editor.
 */
function FilterPopoverContent({ fields, values, onChange, onRemove, initialFieldId, close }) {
  const t = useT();
  const [activeId, setActiveId] = useState(initialFieldId || null);
  const field = fields.find((f) => f.id === activeId);

  if (!field) {
    return (
      <div className="ps-filterpop">
        <div className="ps-filterpop__head">{t('Add filter')}</div>
        <ul className="ps-filterpop__fields">
          {fields.map((f) => {
            const FIcon = f.icon;
            const active = !isFilterEmpty(f, values?.[f.id]);
            return (
              <li key={f.id}>
                <button type="button" className="ps-filterpop__field" onClick={() => setActiveId(f.id)}>
                  {FIcon && <FIcon size={16} className="ps-filterpop__field-icon" />}
                  <span className="ps-filterpop__field-label">{f.label}</span>
                  {active && <span className="ps-filterpop__field-value">{formatFilterValue(f, values[f.id])}</span>}
                  <Icon.ChevronRight size={14} className="ps-filterpop__field-caret" />
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    );
  }

  const empty = isFilterEmpty(field, values?.[field.id]);

  return (
    <div className="ps-filterpop">
      <div className="ps-filterpop__head ps-filterpop__head--editor">
        {!initialFieldId && (
          <button type="button" className="ps-filterpop__back" onClick={() => setActiveId(null)} aria-label={t('Back to fields')}>
            <Icon.ChevronLeft size={16} />
          </button>
        )}
        <span className="ps-filterpop__title">{field.label}</span>
      </div>
      <div className="ps-filterpop__body">
        <FilterValueEditor
          field={field}
          value={values?.[field.id]}
          onChange={(val) => onChange(field.id, val)}
          autoFocus
        />
      </div>
      <div className="ps-filterpop__foot">
        <button
          type="button"
          className="ps-filterpop__clear"
          disabled={empty}
          onClick={() => {
            onRemove?.(field.id);
            if (initialFieldId) close(); else setActiveId(null);
          }}
        >
          {t('Clear')}
        </button>
        <Button size="sm" variant="primary" onClick={close}>{t('Done')}</Button>
      </div>
    </div>
  );
}

/**
 * FilterPopover — the add/edit-a-filter surface.
 *
 * Props:
 *   fields, values, onChange(fieldId, value), onRemove(fieldId)
 *   trigger: ReactNode            — anchor (Add filter button, or a chip)
 *   initialFieldId: string        — open straight to this field's editor (chip edit)
 *   placement, open, onOpenChange — passthrough to Popover
 */
const FilterPopover = ({
  fields = [],
  values = {},
  onChange,
  onRemove,
  trigger,
  initialFieldId,
  placement = 'bottom-start',
  open,
  onOpenChange,
}) => (
  <Popover
    trigger={trigger}
    placement={placement}
    open={open}
    onOpenChange={onOpenChange}
    panelClassName="ps-popover__panel--filter"
  >
    {({ close }) => (
      <FilterPopoverContent
        fields={fields}
        values={values}
        onChange={onChange}
        onRemove={onRemove}
        initialFieldId={initialFieldId}
        close={close}
      />
    )}
  </Popover>
);

export default FilterPopover;
